import { NoteType, TimelineType } from '@prisma/client';

export const useTimeline = () => {
    const getAllTimeline = async (options: {
        contactId?: string;
        businessId?: string;
        categories?: TimelineType[];
        startedAt?: Date;
        endedAt?: Date;
        businesses?: string[];
        contacts?: string[];
        skip: number;
        take: number;
    }) => {
        const params = new URLSearchParams();
        // Allow businesses array without businessId/contactId for dashboard timeline
        if (!options.contactId && !options.businessId && (!options.businesses || options.businesses.length === 0)) {
            throw new Error('Invalid Request');
        }

        if (options.contactId) {
            params.append('contactId', options.contactId.toString());
        }
        if (options.businessId) {
            params.append('businessId', options.businessId.toString());
        }
        options.categories?.forEach((category) =>
            params.append('categories', category)
        );
        if (options.startedAt) {
            params.append('startedAt', options.startedAt.toISOString());
        }
        if (options.endedAt) {
            params.append('endedAt', options.endedAt.toISOString());
        }
        options.businesses?.forEach((business) =>
            params.append('businesses', business)
        );
        options.contacts?.forEach((contact) =>
            params.append('contacts', contact)
        );
        params.append('skip', options.skip.toString());
        params.append('take', options.take.toString());

        const res = await $fetch(
            `/api/v1/timeline/get-all?${params.toString()}`,
            {
                method: 'GET',
            }
        );
        return res;
    };

    const getAllTimelineNotes = async (options: {
        contactId?: string;
        businessId?: string;
        contractId?: string;
        categories?: TimelineType[];
        startedAt?: Date;
        endedAt?: Date;
        businesses?: string[];
        contacts?: string[];
        noteTypes?: NoteType[];
        noteAuthors?: string[];
        skip: number;
        take: number;
    }) => {
        const params = new URLSearchParams();
        if (!options.contactId && !options.businessId && !options.contractId) {
            throw new Error('Invalid Request');
        }

        if (options.contactId) {
            params.append('contactId', options.contactId.toString());
        }
        if (options.businessId) {
            params.append('businessId', options.businessId.toString());
        }
        if (options.contractId) {
            params.append('contractId', options.contractId.toString());
        }
        options.categories?.forEach((category) =>
            params.append('categories', category)
        );
        if (options.startedAt) {
            params.append('startedAt', options.startedAt.toISOString());
        }
        if (options.endedAt) {
            params.append('endedAt', options.endedAt.toISOString());
        }
        options.businesses?.forEach((business) =>
            params.append('businesses', business)
        );
        options.contacts?.forEach((contact) =>
            params.append('contacts', contact)
        );
        options.noteTypes?.forEach((type) =>
            params.append('noteTypes', type)
        );
        options.noteAuthors?.forEach((author) =>
            params.append('noteAuthors', author)
        );
        params.append('skip', options.skip.toString());
        params.append('take', options.take.toString());

        const res = await $fetch(
            `/api/v1/timeline/get-timeline-notes?${params.toString()}`,
            {
                method: 'GET',
            }
        );
        return res;
    };

    const getAllTimelinePinnedNotes = async (options: {
        contactId?: string;
        businessId?: string;
        contractId?: string;
        categories?: TimelineType[];
        startedAt?: Date;
        endedAt?: Date;
        businesses?: string[];
        contacts?: string[];
        noteTypes?: NoteType[];
        noteAuthors?: string[];
    }) => {
        const params = new URLSearchParams();
        if (!options.contactId && !options.businessId && !options.contractId) {
            throw new Error('Invalid Request');
        }

        if (options.contactId) {
            params.append('contactId', options.contactId.toString());
        }
        if (options.businessId) {
            params.append('businessId', options.businessId.toString());
        }
        if (options.contractId) {
            params.append('contractId', options.contractId.toString());
        }
        options.categories?.forEach((category) =>
            params.append('categories', category)
        );
        if (options.startedAt) {
            params.append('startedAt', options.startedAt.toISOString());
        }
        if (options.endedAt) {
            params.append('endedAt', options.endedAt.toISOString());
        }
        options.businesses?.forEach((business) =>
            params.append('businesses', business)
        );
        options.contacts?.forEach((contact) =>
            params.append('contacts', contact)
        );
        options.noteTypes?.forEach((type) =>
            params.append('noteTypes', type)
        );
        options.noteAuthors?.forEach((author) =>
            params.append('noteAuthors', author)
        );

        const res = await $fetch(
            `/api/v1/timeline/get-timeline-pinned-notes?${params.toString()}`,
            {
                method: 'GET',
            }
        );
        return res;
    };

    const getAllTimelineTasks = async (options: {
        contactId?: string;
        businessId?: string;
        categories?: TimelineType[];
        startedAt?: Date;
        endedAt?: Date;
        businesses?: string[];
        contacts?: string[];
        taskAssignedTo?: string[];
        taskStatus?: boolean;
        skip: number;
        take: number;
    }) => {
        const params = new URLSearchParams();
        if (!options.contactId && !options.businessId) {
            throw new Error('Invalid Request');
        }

        if (options.contactId) {
            params.append('contactId', options.contactId.toString());
        }
        if (options.businessId) {
            params.append('businessId', options.businessId.toString());
        }
        options.categories?.forEach((category) =>
            params.append('categories', category)
        );
        if (options.startedAt) {
            params.append('startedAt', options.startedAt.toISOString());
        }
        if (options.endedAt) {
            params.append('endedAt', options.endedAt.toISOString());
        }
        options.businesses?.forEach((business) =>
            params.append('businesses', business)
        );
        options.contacts?.forEach((contact) =>
            params.append('contacts', contact)
        );
        options.taskAssignedTo?.forEach((agent) =>
            params.append('taskAssignedTo', agent)
        );
        if (options.taskStatus) {
            params.append('taskStatus', options.taskStatus.toString());
        }
        params.append('skip', options.skip.toString());
        params.append('take', options.take.toString());

        const res = await $fetch(
            `/api/v1/timeline/get-timeline-tasks?${params.toString()}`,
            {
                method: 'GET',
            }
        );
        return res;
    };

    const getTimelineNotesForDashboard = async (options: {
        categories?: TimelineType[];
        startedAt?: Date;
        endedAt?: Date;
        businesses?: string[];
        contacts?: string[];
        noteTypes?: NoteType[];
        noteAuthors?: string[];
        skip?: number;
        take?: number;
    }) => {
        const params = new URLSearchParams();
        
        options.categories?.forEach((category) =>
            params.append('categories', category)
        );
        if (options.startedAt) {
            params.append('startedAt', options.startedAt.toISOString());
        }
        if (options.endedAt) {
            params.append('endedAt', options.endedAt.toISOString());
        }
        options.businesses?.forEach((business) =>
            params.append('businesses', business)
        );
        options.contacts?.forEach((contact) =>
            params.append('contacts', contact)
        );
        options.noteTypes?.forEach((type) =>
            params.append('noteTypes', type)
        );
        options.noteAuthors?.forEach((author) =>
            params.append('noteAuthors', author)
        );
        if (options.skip !== undefined) {
            params.append('skip', options.skip.toString());
        }
        if (options.take !== undefined) {
            params.append('take', options.take.toString());
        }

        const res = await $fetch(
            `/api/v1/timeline/get-timeline-notes-for-dashboard?${params.toString()}`,
            {
                method: 'GET',
            }
        );
        return res;
    };



    return {
        getAllTimeline,
        getAllTimelineNotes,
        getAllTimelinePinnedNotes,
        getAllTimelineTasks,
        getTimelineNotesForDashboard,
    };
};
